import { NavLink, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { FieldsTab } from './settings/FieldsTab';
import { IntegrationsTab } from './settings/IntegrationsTab';
import { JoiningTab } from './settings/JoiningTab';
import { TeamTab } from './settings/TeamTab';

/**
 * Administering the project, one concern per tab.
 *
 * They are routes rather than local state so each one is a link an admin can
 * send to a colleague, and so the browser's back button walks back out of them.
 */
const TABS = [
  { path: 'fields', label: 'Task fields', element: <FieldsTab /> },
  { path: 'team', label: 'Team', element: <TeamTab /> },
  { path: 'joining', label: 'Joining', element: <JoiningTab /> },
  { path: 'integrations', label: 'Discord', element: <IntegrationsTab /> },
];

export function Settings() {
  // Absolute, built from the team in the URL. A relative `to` would not do: this
  // page is mounted on a splat route, and React Router resolves a relative link
  // against the matched path *including* the splat — so from /settings/fields
  // every tab would link to /settings/fields/<tab> and nest one level per click.
  const { groupKey } = useParams<{ groupKey: string }>();
  const base = `/g/${groupKey}/settings`;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-base font-semibold tracking-tight">Settings</h1>
        <p className="mt-0.5 text-sm text-ink-faint">
          What the project is made of, who works on it, and where its releases are announced.
        </p>
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-line pb-2">
        {TABS.map((tab) => (
          <NavLink
            key={tab.path}
            to={`${base}/${tab.path}`}
            className={({ isActive }) =>
              `rounded-lg px-3 py-1.5 text-sm transition-colors ${
                isActive
                  ? 'bg-elevated font-medium text-ink'
                  : 'text-ink-soft hover:bg-hover hover:text-ink'
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <div className="flex flex-col gap-8">
        <Routes>
          {TABS.map((tab) => (
            <Route key={tab.path} path={tab.path} element={tab.element} />
          ))}
          <Route path="*" element={<Navigate to={`${base}/fields`} replace />} />
        </Routes>
      </div>
    </div>
  );
}
